import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { format, parseISO, setHours, setMinutes } from "date-fns";
import Mustache from "mustache";
import { es } from "date-fns/locale";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  TextField,
  Typography,
  Box,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactSelect from "../ContactSelect";
import WhatsAppSelect from "../WhatsAppSelect";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fullWidthContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  timezoneInfo: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  divider: { margin: theme.spacing(2, 0) },
  previewBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  }
}));

const ScheduleMessageSchema = Yup.object().shape({
  body: Yup.string()
    .min(1, "El mensaje no puede estar vacío")
    .required("Este campo es obligatorio")
    .test(
      "validate-braces",
      "Formato de variable inválido. Use {{variable}} o {{{variable}}}.",
      (value) => {
        if (!value) return true;
        const validVariables = value.match(/{{{?[^{}]+}}}?/g) || [];
        
        let tempValue = value;
        
        validVariables.forEach(variable => {
          tempValue = tempValue.replace(variable, '');
        });
        
        return !tempValue.includes('{') && !tempValue.includes('}');
      }
    ),
  scheduleDate: Yup.date()
    .required("Debe seleccionar una fecha.")
    .test(
      "is-not-past-date",
      "La fecha no puede estar en el pasado.",
      function (value) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return value >= today;
      }
    ),
  scheduleTime: Yup.string()
    .required("Debe seleccionar una hora.")
    .test(
      "is-not-past-time",
      "La hora no puede estar en el pasado.",
      function (value) {
        if (!value) return true; 
        const { scheduleDate } = this.parent;
        if (!scheduleDate) return true;

        const today = new Date();
        const isToday =
          scheduleDate.getDate() === today.getDate() &&
          scheduleDate.getMonth() === today.getMonth() &&
          scheduleDate.getFullYear() === today.getFullYear();
        
        if (!isToday) {
          return true;
        }

        const [hours, minutes] = value.split(':');
        const selectedTime = setHours(setMinutes(new Date(), minutes), hours);

        return selectedTime > new Date();
      }
    ),
    contactId: Yup.number().required("Debe seleccionar un contacto"),
    whatsappId: Yup.number()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null) return null; 
        return Number(originalValue);
      })
      .required("Debe seleccionar una conexión")
      .nullable(),
    recurrence: Yup.string().nullable(),
});

const MessagePreviewUpdater = ({ body, contact, setMessagePreview }) => {
  useEffect(() => {
    let view = {};
    if (contact) {
      view = {
        nombreContacto: contact.name,
        numeroContacto: contact.number,
        emailContacto: contact.email || "",
      };
      if (contact.extraInfo) {
        contact.extraInfo.forEach((info) => (view[info.name] = info.value));
      }
    }
    try {
      const originalEscape = Mustache.escape;
      
      Mustache.escape = text => text;
      
      const renderedText = Mustache.render(body, view);
      
      Mustache.escape = originalEscape;
      
      setMessagePreview(renderedText);
      
    } catch (e) {
      setMessagePreview("Error en el formato de las variables.");
    }
  }, [body, contact, setMessagePreview]);

  return null;
};

const ScheduleMessageModal = ({ open, onClose, scheduledMessageId, ticketId: initialTicketId }) => {
  const classes = useStyles();
  const formikRef = useRef(null);
  const [scheduledMessage, setScheduledMessage] = useState(null);
  const [contact, setContact] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagePreview, setMessagePreview] = useState("");
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { user } = useContext(AuthContext);
  const [cursorPosition, setCursorPosition] = useState(0);
  const bodyInputRef = useRef(null);
  
  const initialValues = {
    body: "",
    contactId: "",
    whatsappId: "",
    recurrence: "NEVER",
    scheduleDate: format(new Date(new Date().getTime() + 60 * 60 * 1000), "yyyy-MM-dd"),
    scheduleTime: format(new Date(new Date().getTime() + 60 * 60 * 1000), "HH:mm"),
  };
  
  const [formInitialValues, setFormInitialValues] = useState(initialValues);

   useEffect(() => {
    const loadData = async () => {
      if (!open) {
        setScheduledMessage(null);
        setContact(null);
        setTicket(null);
        const defaultValues = {
            body: "",
            contactId: "",
            whatsappId: "",
            recurrence: "NEVER",
            scheduleDate: format(new Date(new Date().getTime() + 60 * 60 * 1000), "yyyy-MM-dd"),
            scheduleTime: format(new Date(new Date().getTime() + 60 * 60 * 1000), "HH:mm"),
        };
        setFormInitialValues(defaultValues);
        return;
      }
      setLoading(true);

      if (scheduledMessageId) {
        const { data } = await api.get(`/scheduled-messages/${scheduledMessageId}`);
        setScheduledMessage(data);
        setContact(data.contact);
        setTicket(data.ticket);
        const localSendAt = parseISO(data.sendAt);
        setFormInitialValues({
          body: data.body,
          contactId: data.contactId,
          whatsappId: data.whatsappId,
          recurrence: data.recurrence || "NEVER",
          scheduleDate: format(localSendAt, "yyyy-MM-dd"),
          scheduleTime: format(localSendAt, "HH:mm"),
        });
      } else {
        if (initialTicketId) {
          const { data: ticketData } = await api.get(`/tickets/${initialTicketId}`);
          setTicket(ticketData);
          setContact(ticketData.contact);
          setFormInitialValues((prev) => ({
            ...prev,
            contactId: ticketData.contact.id,
            whatsappId: ticketData.whatsappId,
          }));
        }
      }
      setLoading(false);
    };
    loadData();
  }, [open, scheduledMessageId, initialTicketId]);

  useEffect(() => {
    let vars = ["nombreContacto", "emailContacto"];

    const canViewNumbers = user.profile === "admin" || user.profile === "superadmin" || user.canViewNumbers;

    if (canViewNumbers) {
        vars.push("numeroContacto");
    }

    if (contact?.extraInfo) {
        contact.extraInfo.forEach(info => {
            vars.push(info.name);
        });
    }
    setAvailableVariables(vars);

  }, [contact, user]);

  const getRecurrenceHelperText = (values) => {
    if (!values.scheduleDate || !values.scheduleTime) {
      return "La hora se programará en tu zona horaria local (" + userTimezone + ").";
    }

    const datePart = new Date(values.scheduleDate + "T00:00:00");

    if (isNaN(datePart.getTime())) {
      return "Fecha inválida.";
    }

    try {
        const formattedDate = format(datePart, 'PPP', { locale: es });
        const dayOfWeek = format(datePart, 'EEEE', { locale: es });
        const dayOfMonth = format(datePart, 'do', { locale: es });
        const monthAndDay = format(datePart, "do 'de' MMMM", { locale: es });

        switch (values.recurrence) {
          case 'DAILY':
            return "Se enviará diariamente a las " + values.scheduleTime + ", comenzando el " + formattedDate;
          case 'WEEKLY':
            return "Se enviará cada " + dayOfWeek + " a las " + values.scheduleTime + ", comenzando el " + formattedDate;
          case 'MONTHLY':
            return "Se enviará el día " + dayOfMonth + " de cada mes a las " + values.scheduleTime;
          case 'ANNUALLY':
            return "Se enviará cada " + monthAndDay + " a las " + values.scheduleTime;
          case 'NEVER':
          default:
            return "Se enviará una sola vez el " + formattedDate + " a las " + values.scheduleTime;
        }
    } catch (e) {
        return "Fecha inválida.";
    }
  };
  
  const handleInsertVariable = (tag) => {
    if (!formikRef.current) return;

    const cursorPosition = bodyInputRef.current.selectionStart || 0;
    const currentValue = formikRef.current.values.body;
    
    const newValue = 
        currentValue.substring(0, cursorPosition) + 
        tag + 
        currentValue.substring(cursorPosition);
        
    formikRef.current.setFieldValue('body', newValue);

    setTimeout(() => {
      if(bodyInputRef.current) {
        bodyInputRef.current.focus();
        const newCursorPos = cursorPosition + tag.length;
        bodyInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 100);
  };

  const handleClose = () => onClose();

  const handleSave = async (values) => {
    const datePart = new Date(`${values.scheduleDate}T00:00:00`);
    const [hours, minutes] = values.scheduleTime.split(":");
    let scheduledDate = setHours(datePart, parseInt(hours, 10));
    scheduledDate = setMinutes(scheduledDate, parseInt(minutes, 10));
    const utcDateString = scheduledDate.toISOString();

    const data = {
      body: values.body,
      sendAt: utcDateString,
      contactId: values.contactId,
      whatsappId: values.whatsappId,
      recurrence: values.recurrence === "NEVER" ? null : values.recurrence,
      ticketId: initialTicketId || undefined, 
    };

    try {
      if (scheduledMessageId) {
        await api.put(`/scheduled-messages/${scheduledMessageId}`, data);
        toast.success("Mensaje programado actualizado.");
      } else {
        await api.post(`/scheduled-messages`, data);
        toast.success("Mensaje programado correctamente.");
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };
  
  const shouldRenderContactSection = (!scheduledMessageId && !initialTicketId) || (scheduledMessageId && ticket);  

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          {scheduledMessageId ? "Editar Mensaje Programado (ID: " + scheduledMessageId + ")" : "Programar Nuevo Mensaje"}
        </DialogTitle>
        <Formik
          innerRef={formikRef}
          initialValues={formInitialValues}
          enableReinitialize
          validationSchema={ScheduleMessageSchema}
          onSubmit={async (values, actions) => {
            await handleSave(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values, setFieldTouched }) => (
            <Form>
              <MessagePreviewUpdater
                body={values.body}
                contact={contact}
                setMessagePreview={setMessagePreview}
              />
              <DialogContent dividers>
                {loading ? <Box display="flex" justifyContent="center" my={3}><CircularProgress /></Box> :
                  <>
                    <div className={classes.fullWidthContainer} style={{gap: '16px'}}>
                        { (scheduledMessageId || initialTicketId) ? (
                            <TextField
                                label="Contacto"
                                value={contact?.name || "Cargando..."}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                disabled
                            />
                        ) : (
                            <ContactSelect
                                value={values.contactId}
                                onChange={async (contactId) => {
                                    setFieldValue("contactId", contactId);
                                    if (contactId) {
                                        const { data } = await api.get("/contacts/" + contactId);
                                        setContact(data);
                                    } else {
                                        setContact(null);
                                    }
                                }}
                                error={touched.contactId && Boolean(errors.contactId)}
                                helperText={touched.contactId && errors.contactId}
                                onBlur={() => setFieldTouched("contactId", true)}
                            />
                        )}

                        <WhatsAppSelect
                            value={values.whatsappId}
                            onChange={(whatsappId) => setFieldValue("whatsappId", whatsappId)}
                            error={touched.whatsappId && Boolean(errors.whatsappId)}
                            helperText={touched.whatsappId && errors.whatsappId || (scheduledMessageId && "Puede cambiar la conexión si es necesario.")}
                            mode="select"
                            allowNone={false}
                        />
                    </div>

                    <div className={classes.multFieldLine}>
                      <Field 
                        as={TextField} 
                        label="Fecha de Envío" 
                        type="date" 
                        name="scheduleDate"
                        error={touched.scheduleDate && Boolean(errors.scheduleDate)}
                        helperText={touched.scheduleDate && errors.scheduleDate}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        margin="dense"
                        fullWidth 
                      />
                      <Field 
                        as={TextField} 
                        label="Hora de Envío" 
                        type="time" 
                        name="scheduleTime"
                        error={touched.scheduleTime && Boolean(errors.scheduleTime)}
                        helperText={touched.scheduleTime && errors.scheduleTime}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        margin="dense"
                        fullWidth 
                      />
                    </div>

                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Recurrencia</InputLabel>
                      <Field
                        as={Select}
                        label="Recurrencia"
                        name="recurrence"
                        error={touched.recurrence && Boolean(errors.recurrence)}
                      >
                        <MenuItem value="NEVER">Nunca</MenuItem>
                        <MenuItem value="DAILY">Diariamente</MenuItem>
                        <MenuItem value="WEEKLY">Semanalmente</MenuItem>
                        <MenuItem value="MONTHLY">Mensualmente</MenuItem>
                        <MenuItem value="ANNUALLY">Anualmente</MenuItem>
                      </Field>
                    </FormControl>

                    <Typography variant="caption" className={classes.timezoneInfo}>
                      {getRecurrenceHelperText(values)}
                    </Typography>
                    
                    <Divider className={classes.divider} />
                      
                    <Typography variant="subtitle1" gutterBottom>
                      Variables Disponibles
                    </Typography>
                    <Box className={classes.chipContainer}>
                      {availableVariables.map((variable) => {
                        const tag = "{{" + variable + "}}";
                        return (
                          <Chip
                            key={variable}
                            label={tag}
                            onClick={() => handleInsertVariable(tag)}
                            variant="outlined"
                            size="small"
                          />
                        );
                      })}
                    </Box>
                    
                    <Field
                      as={TextField}
                      label="Mensaje"
                      name="body"
                      inputRef={bodyInputRef}
                      onBlur={(e) => setCursorPosition(e.target.selectionStart || 0)}
                      onClick={(e) => setCursorPosition(e.target.selectionStart || 0)} 
                      error={touched.body && Boolean(errors.body)}
                      helperText={touched.body && errors.body}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      rows={6}
                    />
                    
                    <Box className={classes.previewBox}>
                        <Typography variant="subtitle2" gutterBottom>Vista Previa:</Typography>
                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>{messagePreview}</Typography>
                    </Box>
                  </>
                }
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  Cancelar
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={classes.btnWrapper}>
                  {scheduledMessageId ? "Guardar Cambios" : "Programar"}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ScheduleMessageModal;